import { useEffect, useState } from 'react';
import { Copy, Check, Loader2, QrCode, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { cardTerminalApi } from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';
import { formatMoney } from '@/utils/currency';

type Props = {
  invoiceName: string;
  amount?: number;
  onClose: () => void;
};

/** Simple QR via public chart API — no extra npm dependency. */
function qrUrl(data: string, size = 220) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;
}

export default function PayByLinkModal({ invoiceName, amount, onClose }: Props) {
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState('');
  const [paidAmount, setPaidAmount] = useState(amount || 0);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = (
          await cardTerminalApi.createPayByLink(invoiceName, amount)
        ).data.message as { payment_link: string; amount: number };
        if (cancelled) return;
        setLink(res.payment_link);
        setPaidAmount(res.amount);
      } catch (e) {
        if (!cancelled) setError(getApiErrorMessage(e) || 'Could not create payment link');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invoiceName, amount]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-slate-700" />
            <h3 className="font-semibold text-slate-900">Pay by link / QR</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-center">
          {loading ? (
            <div className="py-10 text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Creating Teya link…
            </div>
          ) : error ? (
            <p className="text-sm text-rose-600 py-6">{error}</p>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Show this QR to the customer, or send the link. Amount{' '}
                <strong>{formatMoney(paidAmount)}</strong>
              </p>
              <div className="flex justify-center">
                <img
                  src={qrUrl(link)}
                  alt="Payment QR"
                  className="w-52 h-52 rounded-xl border border-slate-100"
                />
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
                <code className="text-[11px] text-slate-700 break-all flex-1 text-left">{link}</code>
                <button type="button" onClick={copy} className="p-2 rounded-lg hover:bg-white border border-slate-200">
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                Invoice {invoiceName} will update to Paid when Teya confirms the payment.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
